import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Alert, Snackbar, IconButton } from '@mui/material';
import { ReloadOutlined, InfoCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { appInsights } from 'utils/appInsights';
import { useUserInfo } from 'hooks/useUserInfo';

interface VersionUpdateNotificationProps {
  /** Whether a new version is available */
  hasNewVersion: boolean;
  /** Current version string */
  currentVersion: string;
  /** New version string */
  newVersion: string;
  /** Custom message (optional) */
  message?: string;
  /** Update source for logging/debugging */
  source?: 'signalr' | 'polling' | 'none';
  /** Callback to reload the app */
  onReload: () => void;
  /** Display mode: 'dialog' | 'snackbar' (default: 'snackbar') */
  mode?: 'dialog' | 'snackbar';
  /** Auto-show dialog after X seconds (only for dialog mode) */
  autoShowAfter?: number;
}

/**
 * Component to notify users about new app versions
 * 
 * Supports two modes:
 * 1. Snackbar (non-intrusive, bottom notification)
 * 2. Dialog (more prominent, can be auto-shown after delay)
 * 
 * @example
 * ```tsx
 * <VersionUpdateNotification
 *   hasNewVersion={hasNewVersion}
 *   currentVersion={currentVersion}
 *   newVersion={newVersion}
 *   onReload={reloadApp}
 *   mode="snackbar"
 * />
 * ```
 */
export default function VersionUpdateNotification({
  hasNewVersion,
  currentVersion,
  newVersion,
  message,
  source = 'polling',
  onReload,
  mode = 'snackbar',
  autoShowAfter
}: VersionUpdateNotificationProps) {
  const { userName } = useUserInfo();
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Log when update is detected
  useEffect(() => {
    if (hasNewVersion) {
      console.log(`[VersionNotification] Update detected via ${source}:`, {
        current: currentVersion,
        new: newVersion,
        message
      });
    }
  }, [hasNewVersion, source, currentVersion, newVersion, message]);

  useEffect(() => {
    if (hasNewVersion) {
      if (mode === 'dialog') {
        if (autoShowAfter) {
          // Show dialog after delay
          const timer = setTimeout(() => {
            setOpen(true);
          }, autoShowAfter);
          return () => clearTimeout(timer);
        } else {
          setOpen(true);
        }
      } else {
        // Show snackbar immediately
        setSnackbarOpen(true);
      }
    }
  }, [hasNewVersion, mode, autoShowAfter]);

  const handleReload = () => {
    // Track optional reload in Application Insights
    appInsights.trackEvent({
      name: 'VersionControl_UserClickedReload',
      properties: {
        user: userName || 'Unknown',
        version: newVersion || 'Unknown',
        currentVersion: currentVersion || 'Unknown',
        isMandatory: 'false',
        source: source || 'unknown',
        mode: mode || 'snackbar',
        timestamp: new Date().toISOString()
      }
    });
    
    setOpen(false);
    setSnackbarOpen(false);
    onReload();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    
    // Track dismissal in Application Insights
    appInsights.trackEvent({
      name: 'VersionControl_UserDismissedUpdate',
      properties: {
        user: userName || 'Unknown',
        version: newVersion || 'Unknown',
        currentVersion: currentVersion || 'Unknown',
        source: source || 'unknown',
        timestamp: new Date().toISOString()
      }
    });
    
    // Store dismissed version in sessionStorage (session-level only)
    try {
      sessionStorage.setItem('dismissedVersion', newVersion);
      console.log('[VersionNotification] User dismissed version:', newVersion);
    } catch (error) {
      console.error('[VersionNotification] Failed to store dismissed version:', error);
    }
    
    setSnackbarOpen(false);
  };

  if (!hasNewVersion) {
    return null;
  }

  // Dialog Mode
  if (mode === 'dialog') {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="version-update-dialog-title"
        aria-describedby="version-update-dialog-description"
        PaperProps={{
          sx: {
            minWidth: 400
          }
        }}
      >
        <DialogTitle id="version-update-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReloadOutlined style={{ fontSize: '1.5rem' }} />
          New Version Available
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="version-update-dialog-description">
            {message || 'A new version of the application is available. Please reload to get the latest features and bug fixes.'}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
            Current: {currentVersion}
            <br />
            Latest: {newVersion}
            {source && source !== 'none' && (
              <>
                <br />
                <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                  (detected via {source})
                </span>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Later
          </Button>
          <Button 
            onClick={handleReload} 
            variant="contained" 
            startIcon={<ReloadOutlined />}
            autoFocus
          >
            Reload Now
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Snackbar Mode
  return (
    <Snackbar
      open={snackbarOpen}
      onClose={handleSnackbarClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={null} // Don't auto-hide
      sx={{
        width: { xs: '100%', sm: '80%' },
        maxWidth: { xs: '100%', sm: '80%' }
      }}
    >
      <Alert 
        severity="info"
        variant="filled"
        icon={<InfoCircleOutlined />}
        action={
          <>
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleReload}
              startIcon={<ReloadOutlined />}
              sx={{ 
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}
            >
              RELOAD
            </Button>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleSnackbarClose}
              sx={{ 
                padding: '4px',
                marginLeft: '40px',
                marginRight: 0
              }}
            >
              <CloseOutlined style={{ fontSize: '1.2rem' }} />
            </IconButton>
          </>
        }
        sx={{ 
          width: '100%', 
          alignItems: 'center',
          paddingRight: { xs: '16px', sm: '8px' },
          '& .MuiAlert-action': {
            alignItems: 'center',
            paddingTop: 0,
            paddingLeft: '16px',
            paddingRight: 0,
            marginRight: 0,
            marginLeft: 'auto',
            display: 'flex',
            gap: 0
          },
          '& .MuiAlert-message': {
            display: 'flex',
            alignItems: 'center',
            flex: 1
          }
        }}
      >
        {message || `New version available (${newVersion})`}
        {source === 'signalr' && (
          <span style={{ fontSize: '0.75rem', marginLeft: '8px', opacity: 0.8 }}>
            · instant
          </span>
        )}
      </Alert>
    </Snackbar>
  );
}

